// This controller is always attached to the form element wrapping the
// room selection but it really only comes into play when doing multi
// group room selection.
//
// It's responsible for orchestrating the booking process across multiple
// groups.
stimulusApp.register(
  "rooms-form",
  class extends Stimulus.Controller {
    static outlets = ["rooms-group"]
    static targets = [
      "totalRooms",
      "totalGuestPrice",
      "totalAgencyPrice",
      "bookBtn",
      "startBtn",
      "note",
      "priceClarification",
      "footerTotal",
    ]
    static values = {
      roomsCapacity: Object, // Map room ids to number of free units on the property
      roomCountLabels: Array, // Translated labels for the number of rooms selected
    }

    connect() {
      this.element.addEventListener("hb:room-selected", this.#roomSelected)

      if (!this.hasBookBtnTarget) {
        console.warn("WARNING: Missing bookBtn target in:", this.element)
      }

      this.currency = HB.state.currency()
    }

    roomsGroupOutletConnected() {
      this.#highlightFirstUnselectedGroup()
    }

    disconnect() {
      this.element.removeEventListener("hb:room-selected", this.#roomSelected)
    }

    startChoosing() {
      if (this.roomsGroupOutlets.length > 0) {
        const firstUnselectedGroup = this.roomsGroupOutlets.find(
          (o) => !o.getSelectedRoomData(),
        )
        firstUnselectedGroup.expand({})
      }
    }

    #roomSelected = (e) => {
      const selection = this.#selectedRooms()

      if (this.hasFooterTotalTarget) {
        this.footerTotalTarget.classList.remove("rooms-footer--total_initial")
      }

      const remainingCapacity = this.#remainingCapacity(selection)
      this.roomsGroupOutlets.forEach((outlet) => {
        outlet.updateAvailability(remainingCapacity)
      })

      this.totalRoomsTarget.textContent =
        this.roomCountLabelsValue[this.#countRooms(selection)]

      const totals = this.#totalPrices(selection)
      const formatPrice = (price) =>
        price
          ? accounting.formatMoney(price, {
              precision: 0,
              symbol: this.currency.unit,
              format: this.currency.format,
            })
          : null

      if (this.hasTotalGuestPriceTarget) {
        this.totalGuestPriceTarget.textContent = formatPrice(totals.guest)
      }
      if (this.hasTotalAgencyPriceTarget) {
        this.totalAgencyPriceTarget.textContent = formatPrice(totals.agency)
      }

      this.startBtnTarget.innerText = this.startBtnTarget.dataset.continue

      if (selection.length == this.roomsGroupOutlets.length) {
        this.startBtnTarget.classList.add("hidden")
        this.bookBtnTarget.classList.remove("hidden")
        if (this.hasPriceClarificationTarget) {
          this.priceClarificationTarget.classList.remove("hidden")
        }
        this.bookBtnTarget.removeAttribute("disabled")
        this.roomsGroupOutlets[0].focus() // Bring selection and book button into view
      } else {
        this.bookBtnTarget.setAttribute("disabled", "disabled")
        if (this.hasPriceClarificationTarget) {
          this.priceClarificationTarget.classList.add("hidden")
        }
        const firstUnselectedGroup = this.roomsGroupOutlets.find(
          (outlet) => !outlet.selectedRoom(),
        )
        const lastSelectedGroupEl = e.target
        firstUnselectedGroup.expand({ focusTarget: lastSelectedGroupEl })
      }

      // Update note visibility based on button state
      this.#updateNoteVisibility()

      this.#highlightFirstUnselectedGroup()
    }

    #selectedRooms() {
      return this.roomsGroupOutlets
        .map((outlet) => outlet.getSelectedRoomData())
        .filter((room) => room)
    }

    // Given a list of selected room-card stimulus controllers subtract their rooms'
    // quantities to get the remaining capacity.
    #remainingCapacity(selection) {
      const remainingCapacity = Object.assign({}, this.roomsCapacityValue)
      selection.forEach((room) => {
        this.#subtractCapacity(remainingCapacity, room.roomQuantities)
      })
      return remainingCapacity
    }

    #subtractCapacity(capacity, subtractor) {
      Object.entries(subtractor).forEach(([key, value]) => {
        // null means no limit
        if (capacity.hasOwnProperty(key) && capacity[key] !== null) {
          capacity[key] -= value
        }
      })
    }

    #countRooms(selection) {
      let count = 0
      selection.forEach((room) => {
        Object.values(room.roomQuantities).forEach((quantity) => {
          count += quantity
        })
      })
      return count
    }

    #totalPrices(selection) {
      let guestTotal = 0
      let agencyTotal = 0
      selection.forEach((room) => {
        guestTotal += room.guestPrice || 0
        agencyTotal += room.agencyPrice || 0
      })
      return { guest: guestTotal, agency: agencyTotal }
    }

    #updateNoteVisibility() {
      if (this.hasNoteTarget) {
        const selection = this.#selectedRooms()
        const allRoomsSelected =
          selection.length == this.roomsGroupOutlets.length
        this.noteTarget.classList.toggle("hidden", allRoomsSelected)
      }
    }

    #highlightFirstUnselectedGroup() {
      this.roomsGroupOutlets.map((outlet) => outlet.unhighlight())

      const firstUnselectedGroup = this.roomsGroupOutlets.find(
        (o) => !o.getSelectedRoomData(),
      )
      if (!firstUnselectedGroup) return

      firstUnselectedGroup.highlight()
    }
  },
)
